import { Grid, GridItem, HStack, VStack } from '@chakra-ui/react';
import { isBefore } from 'date-fns';
import React from 'react';
import { useQueryClient } from 'react-query';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { ClearButton, SearchButton } from '../../../components/StyledButton';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useFormItems } from '../../../hooks/formItems';
import {
  NOTICE_CONTEXT_NAMES,
  usePostNoticeSearch,
} from '../../../hooks/queries/notice';
import useToastHook from '../../../hooks/ToastHook';
import { NoticeSearchParam } from '../../../models/gen/noticeSearchParam';
import { NoticeSearchResult } from '../../../models/gen/noticeSearchResult';
import { Formatter } from '../../../utils/FormatUtils';

/**
 * お知らせ検索フォームプロパティ
 */
type NotificationSearchFormProps = unknown;

/**
 * お知らせ検索フォーム
 *
 * @returns
 */
const NotificationSearchForm: React.FunctionComponent<
  NotificationSearchFormProps
> = () => {
  const { actions } = useGlobalStateMachine();
  const queryClient = useQueryClient();
  const { FormDateRange, FormInput, form } = useFormItems<NoticeSearchParam>();
  const postNoticeSearch = usePostNoticeSearch();
  const { toast } = useToastHook();

  const setResults = (data: NoticeSearchResult) =>
    queryClient.setQueryData(NOTICE_CONTEXT_NAMES.POST_NOTICE_SEARCH, data);

  return (
    <VStack
      as="form"
      onSubmit={form.handleSubmit((data) => {
        if (
          TypeUtils.isNonNil(data.dateTo) &&
          TypeUtils.isNonNil(data.dateFrom) &&
          isBefore(
            Formatter.createDate(data.dateTo),
            Formatter.createDate(data.dateFrom)
          )
        ) {
          toast('開始日 < 終了日 で入力して下さい', 'warning');
          return;
        }
        actions.setGuard(true);
        postNoticeSearch.mutate(
          {
            ...data,
            dateFrom: Formatter.date(data.dateFrom, true),
            dateTo: Formatter.date(data.dateTo, true),
            isOutOfPeriodInclude: true,
          },
          {
            onSuccess: (data2) => {
              if (TypeUtils.isNonNil(data2)) {
                setResults(data2);
              }
              actions.setGuard(false);
            },
            onError: (error) => {
              toast(
                error instanceof AppError ? error : 'お知らせ検索に失敗しました'
              );
              actions.setGuard(false);
            },
          }
        );
      })}
      width="100%"
    >
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} width="100%">
        <GridItem margin={2}>
          <FormInput label="タイトル" mode="edit" name="title" />
        </GridItem>
        <GridItem margin={2}>
          <FormDateRange
            datePickerProps={{ dropdownMode: 'select', showYearDropdown: true }}
            forms={[{ name: 'dateFrom' }, { name: 'dateTo' }]}
            label="表示期間"
            mode="edit"
            onClickClear={(name) => form.setValue(name, '')}
          />
        </GridItem>
      </Grid>
      <HStack justifyContent="end" width="100%">
        <ClearButton onClick={() => form.reset()} />
        <SearchButton />
      </HStack>
    </VStack>
  );
};

export default NotificationSearchForm;
